import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ExamCode } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { keys, useEntitlements, useMe, useRevisionPlans } from '../lib/queries';
import { EmptyState, ErrorNotice, Spinner } from '../components/ui';

export function Revision() {
  const { data, isLoading } = useRevisionPlans();
  const { data: me } = useMe();
  const { data: ent } = useEntitlements();
  const qc = useQueryClient();

  const target = me?.user?.examTarget ?? 'CAOP';
  const available: ExamCode[] = target === 'BOTH' ? ['CAOP', 'OPRA'] : [target as ExamCode];
  const [examCode, setExamCode] = useState<ExamCode>(available[0] ?? 'CAOP');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaHit, setQuotaHit] = useState(false);

  const generate = async () => {
    setBusy(true);
    setError(null);
    setQuotaHit(false);
    try {
      await api.post('/api/revision-plans', { examCode });
      await qc.invalidateQueries({ queryKey: keys.plans });
      await qc.invalidateQueries({ queryKey: keys.entitlements });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
        setQuotaHit(err.isQuotaError);
      } else {
        setError('Could not build your timetable.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <Spinner />;

  const latest = data?.plans.find((p) => p.examCode === examCode);
  const remaining = ent?.entitlements.remaining.revision_plan ?? null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-ink">Revision timetable</h1>
          <p className="mt-2 max-w-2xl text-muted">
            Built from your latest benchmark, your exam date and the hours you actually have. Time is
            weighted towards the domains that are both heavily examined and weakest for you.
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {available.length > 1
            ? available.map((code) => (
                <button key={code} type="button" onClick={() => setExamCode(code)} className={examCode === code ? 'btn-primary' : 'btn-secondary'}>
                  {code}
                </button>
              ))
            : null}
          <button type="button" className="btn-primary flex-1 sm:flex-none" onClick={() => void generate()} disabled={busy}>
            {busy ? 'Building…' : latest ? 'Rebuild timetable' : 'Build my timetable'}
          </button>
        </div>
      </header>

      {remaining !== null ? (
        <p className="text-xs text-muted">
          {remaining} timetable{remaining === 1 ? '' : 's'} left this billing period.
        </p>
      ) : null}

      {error ? (
        <div>
          <ErrorNotice title={quotaHit ? 'Upgrade required' : 'Could not build your timetable'} message={error} />
          {quotaHit ? <Link to="/pricing" className="btn-primary mt-3">See plans</Link> : null}
        </div>
      ) : null}

      {!latest ? (
        <EmptyState
          title="No timetable yet"
          body="Complete a baseline assessment first — the timetable is built from your domain results, so it needs something to work with."
          action={<Link to="/dashboard" className="btn-primary">Go to my dashboard</Link>}
        />
      ) : (
        <>
          <section className="card p-4 sm:p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-ink">{latest.examCode} plan</h2>
              <p className="text-sm text-muted">
                {latest.weeks.length} weeks · {latest.weeklyHours}h per week
                {latest.examDate ? ` · exam ${new Date(latest.examDate).toLocaleDateString('en-AU', { dateStyle: 'medium' })}` : ''}
              </p>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted">{latest.summary}</p>
          </section>

          <ol className="space-y-4">
            {latest.weeks.map((week) => (
              <li key={week.weekNumber} className="card p-4 sm:p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-base font-semibold text-ink">Week {week.weekNumber}</h3>
                  <p className="text-sm text-muted">
                    {new Date(week.startDate).toLocaleDateString('en-AU', { dateStyle: 'medium' })} · {week.hours}h
                  </p>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {week.focusDomains.map((d) => (
                    <span key={d} className="badge bg-brand-50 dark:bg-brand-500/15 text-brand-800 dark:text-brand-300">{d}</span>
                  ))}
                </div>

                <ul className="mt-4 list-disc space-y-1.5 pl-5">
                  {week.activities.map((a, i) => (
                    <li key={i} className="text-sm leading-relaxed text-muted">{a}</li>
                  ))}
                </ul>

                <p className="mt-4 rounded-lg bg-raised p-3 text-sm text-muted">
                  <span className="font-semibold text-ink">Milestone: </span>
                  {week.milestone}
                </p>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
